import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, FileDown, RefreshCw, Layers, ShieldCheck, CheckCircle, Percent } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function SvgCompressor() {
  const [inputSvg, setInputSvg] = useState(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="120" height="120">
  <rect x="10.5" y="10.5" width="99.0" height="99.0" rx="20.0" fill="#3b82f6" stroke="#1e3a8a" stroke-width="4.0" />
  <circle cx="60.0" cy="60.0" r="30.0" fill="#ffffff" />
</svg>`);
  const [outputSvg, setOutputSvg] = useState("");
  const [copied, setCopied] = useState(false);
  const [scaleFactor, setScaleFactor] = useState(1.0);
  const [precision, setPrecision] = useState(1);
  const [stats, setStats] = useState({ original: 0, compressed: 0, gzip: 0, percent: 0 });

  const compressSvg = () => {
    if (!inputSvg.trim()) {
      setOutputSvg("");
      setStats({ original: 0, compressed: 0, gzip: 0, percent: 0 });
      return;
    }

    let result = inputSvg;

    // 1. Structural Regex rules to scale width/height attributes if specified
    if (scaleFactor !== 1.0) {
      result = result.replace(/(width|height)=["'](\d+(\.\d+)?)["']/g, (match, attr, val) => {
        const newVal = (parseFloat(val) * scaleFactor).toFixed(1);
        return `${attr}="${newVal}"`;
      });
    }

    // 2. Coordinate precision reducer (very effective for paths)
    // Matches numbers with decimals, e.g., 10.3456
    result = result.replace(/(\d+\.\d+)/g, (match) => {
      const parsed = parseFloat(match);
      if (!isNaN(parsed)) {
        return parseFloat(parsed.toFixed(precision)).toString();
      }
      return match;
    });

    // 3. General whitespace and newline removal
    result = result.replace(/<!--[\s\S]*?-->/g, ""); // Strip comments
    result = result.replace(/\s+/g, " "); // Compress spaces
    result = result.replace(/>\s+</g, "><"); // Trim margins between elements
    result = result.trim();

    setOutputSvg(result);

    const origSize = new Blob([inputSvg]).size;
    const compSize = new Blob([result]).size;
    
    // SVGZ/Gzip compression estimate (usually Gzip compresses XML files down by ~65-75%)
    const gzipEstimate = Math.round(compSize * 0.32);

    const diff = origSize - compSize;
    const pct = origSize > 0 ? (diff / origSize) * 100 : 0;

    setStats({
      original: origSize,
      compressed: compSize,
      gzip: gzipEstimate,
      percent: Math.max(0, parseFloat(pct.toFixed(1)))
    });
  };

  useEffect(() => {
    compressSvg();
  }, [inputSvg, scaleFactor, precision]);

  const handleCopy = () => {
    copyToClipboard(outputSvg).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([outputSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "compressed.svg";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolPageLayout
      title="SVG Compressor Online - Compress SVG Vector Graphics"
      description="Compress SVG files online with real-time vector scale adjustment, custom float precision configuration, and accurate GZIP compression size estimation."
      h1="SVG Compressor"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Input or paste your custom SVG vector markup into the raw XML text block.",
        "Adjust the scaling factor slider to proportionally resize your graphics.",
        "Configure coordinate precision levels to aggressively shorten long coordinate chains.",
        "Download your newly compressed .svg file instantly."
      ]}
      faq={[
        { question: "What is an online SVG compressor and how is it used?", answer: "An online SVG compressor processes raw SVG code or files, shrinking code footprint by scaling down dimensions, compressing spaces, and trimming coordinate fractional precision to decrease payload sizes for faster web rendering." },
        { question: "How does compressing coordinate precision decrease SVG file size?", answer: "SVG files describe shapes using lists of coordinates (e.g., 'M 10.33494 20.91238'). Restricting decimal numbers to 1 or 2 digits drops millions of unnecessary bytes of precision data from the file, significantly reducing the size." },
        { question: "What is an SVGZ file and is it supported by web browsers?", answer: "An SVGZ file is a standard SVG file compressed using gzip compression. All modern web browsers natively support rendering SVGZ files, provided that your web server transmits the correct 'Content-Encoding: gzip' HTTP header." },
        { question: "Does this SVG compressor support drag-and-drop vector uploads?", answer: "Yes. You can paste your SVG text directly, or simply input standard layouts. The output compiles instantly with real-time bytes monitoring." },
        { question: "Will compressing my SVG ruin its responsive behavior?", answer: "No, the compression process retains responsive viewport aspects by preserving or calculating appropriate viewBox coordinates. You can also use the scale factor slider to resize layout constraints." },
        { question: "Is my corporate graphic design shared with any servers?", answer: "Never. All compression calculations and coordinate precision parsing occur entirely in your local browser runtime via client-side JavaScript." },
        { question: "What is the recommended precision setting for highly detailed icons?", answer: "For highly detailed vector illustrations and maps, a decimal precision of 2 or 3 is recommended. For basic UI icons, a precision of 1 or 2 is generally sufficient to shrink sizes without rendering changes." },
        { question: "Does the SVG compressor strip important animation keyframes?", answer: "No. Keyframes, inline CSS styling tags, and standard styling definitions are kept intact unless you manually edit them. We only strip spaces, comments, and coordinate overhead." },
        { question: "How do I ensure my web server serves compressed SVGs correctly?", answer: "Ensure that your nginx, Apache, or Cloudflare servers have GZIP compression enabled for the 'image/svg+xml' mime type, or upload pre-compressed .svgz files directly with correct HTTP headers." },
        { question: "Can I batch compress SVG graphics online?", answer: "You can compress files iteratively using this tool by copying output codes or downloading optimized outputs. It works instantly and offline." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Settings Column */}
          <div className="space-y-5 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Percent className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Compression Engine</span>
            </div>

            {/* Scale Factor */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                <Label>Dimension Scale</Label>
                <span className="text-rose-600 font-bold">{scaleFactor.toFixed(1)}x</span>
              </div>
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                value={scaleFactor}
                onChange={(e) => setScaleFactor(parseFloat(e.target.value))}
                className="w-full accent-rose-600 cursor-pointer"
              />
              <p className="text-[10px] text-slate-400">Proportionally scale width and height attributes.</p>
            </div>

            {/* Decimal Precision */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                <Label>Coordinate Precision</Label>
                <span className="text-rose-600 font-bold">{precision} Dec</span>
              </div>
              <input
                type="range"
                min="0"
                max="5"
                step="1"
                value={precision}
                onChange={(e) => setPrecision(parseInt(e.target.value))}
                className="w-full accent-rose-600 cursor-pointer"
              />
              <p className="text-[10px] text-slate-400">Controls coordinate float decimal length (fewer digits = smaller files).</p>
            </div>

            {/* Real-time stats dashboard */}
            {stats.original > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 pt-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Live Compression Stats</span>
                <div className="space-y-1.5 text-xs text-slate-600 pt-1">
                  <div className="flex justify-between">
                    <span>Original Size:</span>
                    <strong className="text-slate-800">{stats.original} B</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Compressed Size:</span>
                    <strong className="text-slate-800">{stats.compressed} B</strong>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1">
                    <span>Est. GZIP/SVGZ Size:</span>
                    <strong className="text-emerald-700 font-bold">{stats.gzip} B</strong>
                  </div>
                </div>

                {stats.percent > 0 ? (
                  <div className="bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-lg p-2 text-center text-xs font-bold mt-2">
                    Shrunk by {stats.percent}%!
                  </div>
                ) : (
                  <div className="text-[10px] text-slate-400 text-center mt-1">Ready to compress</div>
                )}
              </div>
            )}
          </div>

          {/* Editor Area */}
          <div className="md:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Raw SVG Code Input</Label>
                <Textarea
                  value={inputSvg}
                  onChange={(e) => setInputSvg(e.target.value)}
                  className="font-mono text-[10px] leading-relaxed min-h-[250px] bg-slate-50"
                  placeholder="Paste uncompressed SVG XML here..."
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Compressed SVG Output</Label>
                <Textarea
                  value={outputSvg}
                  readOnly
                  className="font-mono text-[10px] leading-relaxed min-h-[250px] bg-white border-blue-200 focus-visible:ring-blue-500"
                  placeholder="Optimizing parameters automatically..."
                />
              </div>
            </div>

            {/* Action controls */}
            <div className="flex gap-3">
              <Button
                onClick={handleCopy}
                disabled={!outputSvg}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied Compressed Code!" : "Copy Compressed Code"}
              </Button>
              <Button
                onClick={handleDownload}
                disabled={!outputSvg}
                variant="outline"
                className="gap-2 border-slate-300 text-slate-700"
              >
                <FileDown className="w-4 h-4" />
                Download Compressed SVG
              </Button>
            </div>
          </div>
        </div>

        {/* Real-time Render Comparison */}
        {outputSvg && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
            <h4 className="font-bold text-sm uppercase tracking-wider text-slate-800">Visual Compress Validation</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 text-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase">1. Before Compression</span>
                <div className="flex justify-center items-center min-h-[150px] bg-white border border-slate-200 rounded-xl p-3 checkerboard-bg">
                  <div className="max-w-[120px] max-h-[120px]" dangerouslySetInnerHTML={{ __html: inputSvg }} />
                </div>
              </div>

              <div className="space-y-2 text-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase">2. After Compression</span>
                <div className="flex justify-center items-center min-h-[150px] bg-white border border-slate-200 rounded-xl p-3 checkerboard-bg">
                  <div className="max-w-[120px] max-h-[120px]" dangerouslySetInnerHTML={{ __html: outputSvg }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}
